import { BackendApiClient, isBackendConfigured } from '@/api/api-client';
import type { AzDanceBookingParticipantUpsertDto } from '@/api/api-client/service';
import {
  EnumAzDanceBookingParticipantUpsertDtoContactType,
  EnumAzDanceBookingParticipantUpsertDtoRegistrationMode,
} from '@/api/api-client/service';
import type { RegisteredParticipant } from '@/state/stores/registrationStore';
import { useRegistrationStore } from '@/state/stores/registrationStore';
import { applyBookingSessionMeToStore } from '@/lib/applyBookingSessionMeToStore';
import {
  decodeBookingJwtPayload,
  extractEventBookingIdFromBookingJwt,
  setAzDanceBookingToken,
} from '@/lib/azdanceBookingSession';
import {
  mapContactTypeToApi,
  mapContactValueForApi,
  mapCountryToCode,
  mapGenderToApi,
  mapLevelToApi,
  mapRoleToApi,
  parseOptionalAge,
} from '@/lib/mapRegisteredParticipantToAzdanceApi';

const CLIENT_ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

/** Тело для первого шага: `POST /azdance/booking/participants` (bootstrap, без JWT). */
export interface ProfileRegistrationSubmitBody {
  event_id: string;
  participant: Record<string, unknown>;
}

function participantPayloadToUpsertRow(
  p: Record<string, unknown>,
): AzDanceBookingParticipantUpsertDto {
  const contactRaw = p.contactType as string | undefined;
  let contactType = EnumAzDanceBookingParticipantUpsertDtoContactType.telegram;
  if (contactRaw === EnumAzDanceBookingParticipantUpsertDtoContactType.instagram) {
    contactType = EnumAzDanceBookingParticipantUpsertDtoContactType.instagram;
  } else if (contactRaw === EnumAzDanceBookingParticipantUpsertDtoContactType.whatsapp) {
    contactType = EnumAzDanceBookingParticipantUpsertDtoContactType.whatsapp;
  } else if (contactRaw === EnumAzDanceBookingParticipantUpsertDtoContactType.telegram) {
    contactType = EnumAzDanceBookingParticipantUpsertDtoContactType.telegram;
  }

  const regRaw = p.registrationMode as string | undefined;
  const registrationMode =
    regRaw === EnumAzDanceBookingParticipantUpsertDtoRegistrationMode.championship
      ? EnumAzDanceBookingParticipantUpsertDtoRegistrationMode.championship
      : EnumAzDanceBookingParticipantUpsertDtoRegistrationMode.default;

  return {
    firstName: String(p.firstName ?? '').trim(),
    lastName: String(p.lastName ?? '').trim(),
    email: String(p.email ?? '').trim(),
    phone: p.phone ? String(p.phone).trim() : undefined,
    countryCode: p.countryCode ? String(p.countryCode).trim() : undefined,
    city: p.city ? String(p.city).trim() : undefined,
    age: parseOptionalAge(p.age as string | number | undefined),
    gender: p.gender as AzDanceBookingParticipantUpsertDto['gender'],
    role: p.role as AzDanceBookingParticipantUpsertDto['role'],
    level: p.level as AzDanceBookingParticipantUpsertDto['level'],
    copartnerId:
      p.copartnerId != null && String(p.copartnerId).trim()
        ? String(p.copartnerId).trim()
        : undefined,
    lookingForPartner:
      p.lookingForPartner === null || p.lookingForPartner === undefined
        ? undefined
        : Boolean(p.lookingForPartner),
    contactType,
    contactValue: p.contactValue ? String(p.contactValue).trim() : undefined,
    registrationMode,
  };
}

export function registeredParticipantToProfilePayload(
  p: RegisteredParticipant,
): Record<string, unknown> {
  return {
    firstName: p.firstName.trim(),
    lastName: p.lastName.trim(),
    email: p.email.trim(),
    phone: (p.phone ?? '').trim() || undefined,
    countryCode: mapCountryToCode(p.country),
    city: p.city?.trim() || undefined,
    age: parseOptionalAge(p.age),
    gender: mapGenderToApi(p.gender),
    role: mapRoleToApi(p.role),
    level: mapLevelToApi(p.level),
    copartnerId:
      p.partnerId && /^[0-9A-Za-z]{26}$/.test(p.partnerId.trim())
        ? p.partnerId.trim()
        : null,
    lookingForPartner:
      p.lookingForPartner === undefined ? null : p.lookingForPartner,
    contactType: mapContactTypeToApi(p.contactType, p.instagram),
    contactValue: mapContactValueForApi(p),
    registeredAt: p.registeredAt,
    registrationMode:
      p.isChampionshipParticipant === true
        ? EnumAzDanceBookingParticipantUpsertDtoRegistrationMode.championship
        : EnumAzDanceBookingParticipantUpsertDtoRegistrationMode.default,
  };
}

export type SubmitProfileRegistrationResult =
  | { ok: true; skipped: true }
  | { ok: true; serverProfileId: string; accessToken: string }
  | { ok: false; error: string };

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}`;
}

/**
 * Первый шаг регистрации: `POST /azdance/booking/participants` (bootstrap: только `event_id` + один primary, без JWT и без reCAPTCHA).
 * После успеха: JWT в куку, `GET /azdance/profile/me` синхронизирует `dashboardEventBookingId`.
 */
export async function submitProfileRegistrationToBackend(
  body: ProfileRegistrationSubmitBody,
  language: string,
): Promise<SubmitProfileRegistrationResult> {
  if (!isBackendConfigured()) {
    if (typeof window !== 'undefined') {
      console.warn(
        '[booking-participants-bootstrap] Backend API is not configured — POST skipped.',
      );
    }
    return { ok: true, skipped: true };
  }

  const row = participantPayloadToUpsertRow(body.participant);
  const optionalId =
    typeof body.participant.id === 'string' &&
    CLIENT_ULID_RE.test(body.participant.id.trim())
      ? body.participant.id.trim()
      : undefined;

  const participants: AzDanceBookingParticipantUpsertDto[] = [
    optionalId ? { ...row, id: optionalId } : { ...row },
  ];

  try {
    const res = await BackendApiClient.AzdanceService.azdanceControllerUpsertBookingParticipants(
      {
        language,
        idempotencyKey: generateIdempotencyKey(),
        body: {
          event_id: body.event_id,
          participants,
        },
      },
      {} as any,
    );

    const accessToken = (res.accessToken ?? '').trim();
    const serverProfileId =
      (res.participantId ?? '').trim() ||
      (accessToken ? decodeBookingJwtPayload(accessToken)?.sub?.trim() ?? '' : '');

    if (!serverProfileId || !accessToken) {
      return { ok: false, error: 'Profile save failed' };
    }

    setAzDanceBookingToken(accessToken);
    const eid =
      (res.eventBookingId ?? '').trim() ||
      extractEventBookingIdFromBookingJwt(accessToken) ||
      null;
    useRegistrationStore.getState().setDashboardEventBookingId(eid);
    await applyBookingSessionMeToStore(language);

    return { ok: true, serverProfileId, accessToken };
  } catch (e: unknown) {
    if (typeof e === 'string') return { ok: false, error: e };
    if (e && typeof e === 'object') {
      const o = e as Record<string, unknown>;
      if (Array.isArray(o.message)) {
        return { ok: false, error: (o.message as unknown[]).map(String).join('; ') };
      }
      if (typeof o.message === 'string') return { ok: false, error: o.message };
      if (typeof o.error === 'string') return { ok: false, error: o.error };
    }
    return { ok: false, error: 'Profile save failed' };
  }
}
